// ─── ComitClientPicker ──────────────────────────────────────────
// Typeahead sur les clients ComIT (Telipso Tâches) pour lier un client RMM à
// son équivalent ComIT. Même ergonomie que ClientPicker (mono-sélection), mais
// la source est distante : chaque frappe interroge ComIT via le proxy
// /platform/comit/clients — aucune liste complète n'est chargée en mémoire.
//
// Props :
//   value     : { id: number, name: string } | null
//   onChange  : (next) => void   (null = liaison retirée)
//   disabled  : bool

import { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { api } from '../lib/api.jsx';

const LIMIT = 25;
const DEBOUNCE_MS = 250;

export function ComitClientPicker({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // `false` quand la plateforme n'a pas de clé ComIT : on le dit plutôt que
  // d'afficher un « aucun résultat » trompeur.
  const [configured, setConfigured] = useState(true);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoading(true);
    const t = setTimeout(async () => {
      const q = query.trim();
      const r = await api.get(
        '/platform/comit/clients?limit=' + LIMIT + (q ? '&q=' + encodeURIComponent(q) : '')
      );
      if (!active) return;
      setConfigured(r ? r.configured !== false : true);
      setResults(r && Array.isArray(r.items) ? r.items : []);
      setLoading(false);
    }, DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [query, open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const select = (c) => {
    onChange({ id: c.idclient, name: c.nomclient });
    setQuery('');
    setOpen(false);
  };

  const label = value && value.id > 0 ? value.name || 'Client ComIT #' + value.id : '';

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className={`bg-sidebar border rounded-lg px-3 py-2 flex items-center gap-2 min-h-[42px] ${
          open ? 'border-blue-500' : 'border-rule'
        } ${disabled ? 'opacity-60 cursor-default' : 'cursor-text'}`}
        onClick={() => {
          if (!disabled) {
            setOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        {label && !open && <span className="text-fg-2 text-[13px] font-medium flex-1">{label}</span>}

        {(open || !label) && (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => !disabled && setOpen(true)}
            placeholder={label ? '' : 'Rechercher un client ComIT…'}
            disabled={disabled}
            className="bg-transparent border-none outline-none text-fg-2 text-[13px] flex-1 min-w-[80px]"
          />
        )}

        {label && !open && !disabled && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(null);
            }}
            className="bg-transparent border-none text-muted-2 cursor-pointer p-0 inline-flex items-center hover:text-fg-2"
            title="Retirer la liaison ComIT"
          >
            <X size={14} strokeWidth={2} />
          </button>
        )}

        <Search size={13} strokeWidth={1.75} className="text-muted-3" />
      </div>

      {open && (
        <div className="absolute z-[60] left-0 right-0 mt-1 bg-card border border-rule rounded-lg shadow-2xl max-h-[260px] overflow-y-auto">
          {!configured ? (
            <div className="px-3 py-2 text-xs text-muted-2">
              Intégration ComIT non configurée (Comit:BaseUrl / Comit:ApiKey).
            </div>
          ) : loading && results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-2">Recherche…</div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2 text-xs text-muted-2">
              {query ? 'Aucun client ComIT trouvé' : 'Tapez pour rechercher'}
            </div>
          ) : (
            results.map((c) => (
              <button
                key={c.idclient}
                type="button"
                onClick={() => select(c)}
                className="w-full text-left bg-transparent border-none px-3 py-1.5 text-xs text-fg-2 cursor-pointer hover:bg-rule"
              >
                {c.nomclient}
                <span className="text-muted-3 ml-2 font-mono text-[10px]">#{c.idclient}</span>
              </button>
            ))
          )}
          {!query && configured && results.length === LIMIT && (
            <div className="px-3 py-1.5 text-[10px] text-muted-3 border-t border-rule">
              Tapez pour affiner — {LIMIT} premiers affichés
            </div>
          )}
        </div>
      )}
    </div>
  );
}
